import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { adminService } from "../../services/index"

// Async thunks for admin operations
/** @type {import("@reduxjs/toolkit").AsyncThunk<any, Record<string, unknown> | void, {}>} */
export const fetchUsers = createAsyncThunk("admin/fetchUsers", async (params = {}, { rejectWithValue }) => {
  try {
    const response = await adminService.getUsers(params)
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to fetch users")
  }
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const createUser = createAsyncThunk("admin/createUser", async (userData, { rejectWithValue }) => {
  try {
    const response = await adminService.createUser(userData)
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to create user")
  }
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const updateUser = createAsyncThunk("admin/updateUser", async ({ id, userData }, { rejectWithValue }) => {
  try {
    const response = await adminService.updateUser(id, userData)
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to update user")
  }
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, string, {}>} */
export const deleteUser = createAsyncThunk("admin/deleteUser", async (userId, { rejectWithValue }) => {
  try {
    await adminService.deleteUser(userId)
    return userId
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to delete user")
  }
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, string, {}>} */
export const toggleUserStatus = createAsyncThunk("admin/toggleUserStatus", async (userId, { rejectWithValue }) => {
  try {
    const response = await adminService.toggleUserStatus(userId)
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to toggle user status")
  }
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, void, {}>} */
export const fetchSystemStats = createAsyncThunk("admin/fetchSystemStats", async (_, { rejectWithValue }) => {
  try {
    const response = await adminService.getSystemStats()
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || "Failed to fetch system stats")
  }
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, Record<string, unknown> | void, {}>} */
export const fetchUserActivity = createAsyncThunk(
  "admin/fetchUserActivity",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await adminService.getUserActivity(params)
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch user activity")
    }
  },
)

const initialState = {
  users: {
    data: [],
    total: 0,
    currentPage: 1,
    totalPages: 1,
    loading: false,
    error: null,
  },
  systemStats: {
    data: null,
    loading: false,
    error: null,
  },
  userActivity: {
    data: [],
    loading: false,
    error: null,
  },
  selectedUser: null,
  filters: {
    role: "all",
    status: "all",
    search: "",
  },
  modals: {
    createUser: false,
    editUser: false,
    deleteUser: false,
  },
}

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload
    },
    clearSelectedUser: (state) => {
      state.selectedUser = null
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = initialState.filters
    },
    openModal: (state, action) => {
      state.modals[action.payload] = true
    },
    closeModal: (state, action) => {
      state.modals[action.payload] = false
    },
    closeAllModals: (state) => {
      Object.keys(state.modals).forEach((key) => {
        state.modals[key] = false
      })
    },
    clearErrors: (state) => {
      state.users.error = null
      state.systemStats.error = null
      state.userActivity.error = null
    },
  },
  extraReducers: (builder) => {
    // Fetch Users
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.users.loading = true
        state.users.error = null
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.users.loading = false
        state.users.data = action.payload.users
        state.users.total = action.payload.total
        state.users.currentPage = action.payload.currentPage
        state.users.totalPages = action.payload.totalPages
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.users.loading = false
        state.users.error = action.payload
      })

    // Create User
    builder
      .addCase(createUser.pending, (state) => {
        state.users.loading = true
        state.users.error = null
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.users.loading = false
        state.users.data.unshift(action.payload.user)
        state.users.total += 1
        state.modals.createUser = false
      })
      .addCase(createUser.rejected, (state, action) => {
        state.users.loading = false
        state.users.error = action.payload
      })

    // Update User
    builder
      .addCase(updateUser.pending, (state) => {
        state.users.loading = true
        state.users.error = null
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.users.loading = false
        const index = state.users.data.findIndex((user) => user._id === action.payload.user._id)
        if (index !== -1) {
          state.users.data[index] = action.payload.user
        }
        state.selectedUser = action.payload.user
        state.modals.editUser = false
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.users.loading = false
        state.users.error = action.payload
      })

    // Delete User
    builder
      .addCase(deleteUser.pending, (state) => {
        state.users.loading = true
        state.users.error = null
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.users.loading = false
        state.users.data = state.users.data.filter((user) => user._id !== action.payload)
        state.users.total -= 1
        state.selectedUser = null
        state.modals.deleteUser = false
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.users.loading = false
        state.users.error = action.payload
      })

    // Toggle User Status
    builder.addCase(toggleUserStatus.fulfilled, (state, action) => {
      const index = state.users.data.findIndex((user) => user._id === action.payload.user._id)
      if (index !== -1) {
        state.users.data[index] = action.payload.user
      }
    })

    // Fetch System Stats
    builder
      .addCase(fetchSystemStats.pending, (state) => {
        state.systemStats.loading = true
        state.systemStats.error = null
      })
      .addCase(fetchSystemStats.fulfilled, (state, action) => {
        state.systemStats.loading = false
        state.systemStats.data = action.payload
      })
      .addCase(fetchSystemStats.rejected, (state, action) => {
        state.systemStats.loading = false
        state.systemStats.error = action.payload
      })

    // Fetch User Activity
    builder
      .addCase(fetchUserActivity.pending, (state) => {
        state.userActivity.loading = true
        state.userActivity.error = null
      })
      .addCase(fetchUserActivity.fulfilled, (state, action) => {
        state.userActivity.loading = false
        state.userActivity.data = action.payload.activities
      })
      .addCase(fetchUserActivity.rejected, (state, action) => {
        state.userActivity.loading = false
        state.userActivity.error = action.payload
      })
  },
})

export const {
  setSelectedUser,
  clearSelectedUser,
  setFilters,
  clearFilters,
  openModal,
  closeModal,
  closeAllModals,
  clearErrors,
} = adminSlice.actions

export default adminSlice.reducer
